import { apiFetch, apiPost } from "@/lib/apiClient";

const ALLOWED_ICON_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/svg+xml",
]);

const MAX_ICON_BYTES = 6 * 1024 * 1024;

export function isAllowedCrmLeadFieldIconFile(file: File): boolean {
  const type = (file.type || "").toLowerCase();
  if (type && ALLOWED_ICON_TYPES.has(type)) return true;
  const name = file.name.toLowerCase();
  return (
    name.endsWith(".png") ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".webp") ||
    name.endsWith(".svg")
  );
}

export async function uploadCrmLeadFieldOptionIcon(orgId: string, file: File): Promise<string> {
  if (!orgId.trim()) {
    throw new Error("Organization context is required to upload an icon.");
  }
  if (!isAllowedCrmLeadFieldIconFile(file)) {
    throw new Error("Use PNG, SVG, WebP, or JPEG.");
  }
  if (file.size > MAX_ICON_BYTES) {
    throw new Error("Icon must be under 6 MB.");
  }

  const fileType = file.type || "image/png";
  const presignRes = await apiPost<{
    presignedUrl?: string;
    url?: string;
    error?: string;
  }>("/api/uploads/organization-branding", {
    orgId: orgId.trim(),
    fileName: file.name || "social-icon.png",
    fileType,
    fileSize: file.size,
    assetType: "icon",
  });

  if (!presignRes.ok || !presignRes.data?.presignedUrl || !presignRes.data?.url) {
    throw new Error(presignRes.data?.error || presignRes.error || "Could not start upload.");
  }

  const putRes = await fetch(presignRes.data.presignedUrl, {
    method: "PUT",
    headers: { "Content-Type": fileType },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error("Upload failed.");
  }

  return presignRes.data.url;
}

const TENANT_ASSETS_BUCKET_ORG = /^atw-org-assets-(\d{7})-/i;

/** Parse org id from a tenant assets bucket hostname (super_admin sessions often omit user.orgId). */
export function extractOrgIdFromTenantAssetUrl(storedUrl: string | null | undefined): string | null {
  const src = (storedUrl || "").trim();
  if (!src) return null;
  try {
    const match = new URL(src).hostname.match(TENANT_ASSETS_BUCKET_ORG);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

export function resolveCrmLeadFieldIconOrgId(
  orgId: string | null | undefined,
  storedUrl: string | null | undefined,
): string | null {
  const fromSession = (orgId || "").trim();
  if (fromSession) return fromSession;
  return extractOrgIdFromTenantAssetUrl(storedUrl);
}

function isPrivateTenantAssetUrl(src: string): boolean {
  if (!src.includes(".amazonaws.com") || !src.includes(".s3.")) return false;
  return TENANT_ASSETS_BUCKET_ORG.test(new URL(src).hostname);
}

/** Resolve tenant branding URLs for use in img src (private buckets). */
export async function resolveCrmLeadFieldIconDisplayUrl(
  orgId: string | null | undefined,
  storedUrl: string | null | undefined,
): Promise<string | null> {
  const src = (storedUrl || "").trim();
  if (!src) return null;
  if (src.startsWith("blob:") || src.startsWith("data:")) return src;

  let privateTenant = false;
  try {
    privateTenant = isPrivateTenantAssetUrl(src);
  } catch {
    privateTenant = false;
  }

  const tenantId = resolveCrmLeadFieldIconOrgId(orgId, src);
  if (!tenantId) {
    return privateTenant ? null : src;
  }
  try {
    const res = await apiFetch(
      `/api/uploads/organization-branding?orgId=${encodeURIComponent(tenantId)}&url=${encodeURIComponent(src)}`,
    );
    if (!res.ok) return privateTenant ? null : src;
    const data = (await res.json()) as { url?: string };
    const signed = data.url?.trim();
    if (signed) return signed;
    return privateTenant ? null : src;
  } catch {
    return privateTenant ? null : src;
  }
}
